import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Bot, Plus, Search, Zap, Save, ChevronLeft, X,
  Loader2, Settings2, Code2, FileText, Cpu, Pencil,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const CATEGORIES = ["All", "Audit", "Generation", "Operational"];
const catColors = { Audit: "from-[#ef4444] to-[#f59e0b]", Generation: "from-[#1a6fc4] to-[#2d9cdb]", Operational: "from-[#0f172a] to-[#475569]" };

export const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("library");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("All");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Builder state
  const [form, setForm] = useState({ name: "", description: "", category: "Generation", model: "gpt-5.2", system_prompt: "", input_schema: "", output_schema: "" });

  const fetchAgents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/agents`, { withCredentials: true });
      setAgents(Array.isArray(data) ? data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const openBuilder = (agent) => {
    if (agent) {
      setForm({ name: agent.name, description: agent.description, category: agent.category, model: agent.model || "gpt-5.2", system_prompt: agent.system_prompt || "", input_schema: agent.input_schema || "", output_schema: agent.output_schema || "" });
      setEditing(agent);
    } else {
      setForm({ name: "", description: "", category: "Generation", model: "gpt-5.2", system_prompt: "", input_schema: "", output_schema: "" });
      setEditing(null);
    }
    setView("builder");
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/agents/${editing.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/agents`, form, { withCredentials: true });
      }
      await fetchAgents();
      setView("library");
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const filtered = agents?.filter(a => {
    if (search && !a.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (catFilter !== "All" && a.category !== catFilter) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-full" data-testid="agents-loading"><Loader2 className="w-6 h-6 animate-spin text-foreground/45" /></div>;

  if (view === "builder") {
    return (
      <div className="flex flex-col h-full" data-testid="agent-builder">
        <div className="flex items-center gap-3 px-6 py-3 bg-white border-b border-border/40">
          <button onClick={() => setView("library")} className="text-xs text-foreground/40 hover:text-foreground flex items-center gap-1" data-testid="back-to-agents">
            <ChevronLeft className="w-4 h-4" /> Agents
          </button>
          <div className="w-px h-5 bg-border/40" />
          <h3 className="font-heading font-bold text-sm text-foreground">{editing ? "Edit Agent" : "New Agent"}</h3>
          <div className="flex-1" />
          <button onClick={handleSave} disabled={saving || !form.name.trim()} className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-[#014d7d] text-white disabled:opacity-40 flex items-center gap-1.5" data-testid="save-agent-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Agent
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="max-w-2xl space-y-5">
            {/* Basic info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Agent Name</label>
                <input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Assessment Generator"
                  className="w-full h-10 px-3 text-sm border border-border/60 rounded-xl outline-none" data-testid="agent-name-input" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Category</label>
                <select value={form.category} onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}
                  className="w-full h-10 px-3 text-sm border border-border/60 rounded-xl outline-none bg-white" data-testid="agent-category-select">
                  <option>Audit</option><option>Generation</option><option>Operational</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Description</label>
              <textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="What does this agent do?"
                className="w-full text-sm border border-border/60 rounded-xl px-3 py-2.5 outline-none resize-none" rows={3} data-testid="agent-desc-input" />
            </div>

            <div>
              <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Model</label>
              <select value={form.model} onChange={(e) => setForm(p => ({ ...p, model: e.target.value }))}
                className="w-full h-10 px-3 text-sm border border-border/60 rounded-xl outline-none bg-white" data-testid="agent-model-select">
                <option>gpt-5.2</option><option>gpt-4o</option><option>claude-sonnet-4.5</option><option>gemini-3-flash</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">System Prompt</label>
              <textarea value={form.system_prompt} onChange={(e) => setForm(p => ({ ...p, system_prompt: e.target.value }))} placeholder="Instructions for the agent..."
                className="w-full text-sm border border-border/60 rounded-xl px-3 py-2.5 outline-none resize-none font-mono text-xs leading-relaxed" rows={6} data-testid="agent-prompt-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Input Schema</label>
                <textarea value={form.input_schema} onChange={(e) => setForm(p => ({ ...p, input_schema: e.target.value }))} placeholder='{"content": "string", "chapter": "number"}'
                  className="w-full text-xs border border-border/60 rounded-xl px-3 py-2.5 outline-none resize-none font-mono" rows={4} data-testid="agent-input-schema" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1.5 block">Output Schema</label>
                <textarea value={form.output_schema} onChange={(e) => setForm(p => ({ ...p, output_schema: e.target.value }))} placeholder='{"items": ["MCQ"], "count": "number"}'
                  className="w-full text-xs border border-border/60 rounded-xl px-3 py-2.5 outline-none resize-none font-mono" rows={4} data-testid="agent-output-schema" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="agents-page">
      <div className="px-6 pt-5 pb-4 bg-white border-b border-border/40">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg text-foreground">Agents</h2>
          <button onClick={() => openBuilder(null)} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#014d7d] text-white flex items-center gap-1.5" data-testid="new-agent-btn">
            <Plus className="w-3.5 h-3.5" /> New Agent
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-foreground/45" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search agents..."
              className="w-full h-10 pl-10 pr-4 text-sm border border-border/60 rounded-xl bg-white outline-none placeholder:text-foreground/45" data-testid="agent-search" />
          </div>
          <div className="flex bg-[#f0f4f8] rounded-full p-0.5 border border-border/40">
            {CATEGORIES?.map(c => (
              <button key={c} onClick={() => setCatFilter(c)}
                className={`text-[10px] font-semibold px-3 py-1 rounded-full ${catFilter === c ? "bg-white text-foreground shadow-sm" : "text-foreground/40"}`}
                data-testid={`filter-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="agent-grid">
          {filtered?.map(a => (
            <div key={a.id} className="bg-white border border-border/60 rounded-2xl overflow-hidden hover:shadow-soft group" data-testid={`agent-card-${a.id}`}>
              <div className={`h-1.5 bg-gradient-to-r ${catColors[a.category] || catColors.Generation}`} />
              <div className="p-5">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-[#014d7d]/8 flex items-center justify-center"><Bot className="w-4 h-4 text-[#014d7d]" /></div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground">{a.name}</h3>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-foreground/[0.04] text-foreground/40">{a.category}</span>
                    </div>
                  </div>
                  <button onClick={() => openBuilder(a)} className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg text-foreground/45 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center" data-testid={`edit-agent-${a.id}`}>
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-xs text-foreground/40 leading-relaxed mb-3 line-clamp-2">{a.description}</p>
                <div className="flex items-center gap-3 text-[10px] text-foreground/50">
                  <span className="flex items-center gap-1"><Cpu className="w-3 h-3" /> {a.model || "gpt-5.2"}</span>
                  {a.system_prompt && <span className="flex items-center gap-1"><Code2 className="w-3 h-3" /> Prompted</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
